import React from 'react';
import CloseIcon from '@mui/icons-material/Close';
import SageIcon from './icons/SageIcon';
import SageContextPicker from './SageContextPicker';
import {
  DEFAULT_SAGE_LANGUAGE,
  SAGE_LANGUAGES,
} from '../utils/sageLanguages';

function SageOrb({ size = 72, className = '' }) {
  return <SageIcon size={size} className={className} />;
}

function SagePanelHeader({ subtitle, onClose, online = true }) {
  return (
    <header className="gg-sage-panel__header">
      <div className="gg-sage-panel__brand">
        <SageOrb size={36} />
        <div>
          <div className="gg-sage-panel__name-row">
            <span className="gg-sage-panel__name">Sage</span>
            {online ? <span className="gg-sage-panel__online" title="Online" /> : null}
          </div>
          <div className="gg-sage-panel__role">{subtitle}</div>
        </div>
      </div>
      <button
        type="button"
        className="gg-sage-panel__close"
        onClick={onClose}
        aria-label="Close Sage panel"
      >
        <CloseIcon fontSize="small" />
      </button>
    </header>
  );
}

function SageIntro({
  onAllow,
  onMaybeLater,
  busy = false,
  languages = ['English'],
  defaultLanguage = 'English',
}) {
  const [language, setLanguage] = React.useState(defaultLanguage);

  React.useEffect(() => {
    setLanguage(defaultLanguage);
  }, [defaultLanguage]);

  return (
    <div className="gg-sage-intro">
      <div className="gg-sage-intro__scroll">
        <div className="gg-sage-intro__hero">
          <div className="gg-sage-intro__orb-wrap">
            <SageOrb size={56} />
          </div>
          <h2 className="gg-sage-intro__title">
            Meet <span>Sage</span>,
          </h2>
          <p className="gg-sage-intro__subtitle">your AI interview copilot</p>
          <p className="gg-sage-intro__body">
            Sage listens live and only steps in when something contradicts the
            candidate&apos;s background or prior answers, or when you need
            technical help evaluating a response.
          </p>
        </div>

        <div className="gg-sage-lang">
          <label className="gg-sage-lang__label" htmlFor="sage-language">
            Interview language
          </label>
          <p className="gg-sage-lang__hint">
            Sage will listen and suggest in this language.
          </p>
          <select
            id="sage-language"
            className="gg-sage-lang__select"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            disabled={busy}
          >
            {languages.map((lang) => (
              <option key={lang} value={lang}>
                {lang}
              </option>
            ))}
          </select>
        </div>

        <div className="gg-sage-next">
          <div className="gg-sage-next__label">Next steps</div>
          <ol className="gg-sage-next__list">
            <li>Choose the interview language above</li>
            <li>Optionally pick candidate context (resume / prior AI interview)</li>
            <li>Sage joins the room and starts listening</li>
          </ol>
        </div>
      </div>

      <div className="gg-sage-intro__actions">
        <button
          type="button"
          className="gg-sage-intro__allow"
          onClick={() => onAllow?.(language)}
          disabled={busy}
        >
          {busy ? 'Inviting Sage…' : 'Continue'}
        </button>
        <button
          type="button"
          className="gg-sage-intro__later"
          onClick={onMaybeLater}
          disabled={busy}
        >
          Maybe later
        </button>
      </div>
    </div>
  );
}

function priorityColor(priority) {
  if (priority === 'high') return '#dc2626';
  if (priority === 'low') return '#9ca3af';
  return '#9048fb';
}

/**
 * Sage panel — intro consent UI or live suggestions (light Getgeek style).
 */
export default function CopilotSidebar({
  isOpen,
  onClose,
  suggestions = [],
  ready = false,
  sessionComplete = false,
  latestNotes = '',
  redFlags = [],
  onClear,
  onDismiss,
  docked = false,
  mode = 'active', // 'intro' | 'active' | 'context'
  onAllowJoin,
  inviting = false,
  candidateName,
  resumeMatch,
  assessmentLabel,
  contextPicker = null,
}) {
  if (!isOpen) {
    return null;
  }

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (e) {
      console.error('Copy failed', e);
    }
  };

  const statusLabel =
    mode === 'intro'
      ? 'AI Assistant'
      : mode === 'context'
        ? 'Choose context'
        : sessionComplete
          ? 'Session ended'
          : ready
            ? 'Listening'
            : inviting
              ? 'Joining…'
              : 'AI Assistant';

  const activeBody = (
    <>
      {(latestNotes || (redFlags && redFlags.length > 0)) && (
        <div className="gg-sage-panel__meta">
          {latestNotes ? <p className="gg-sage-panel__notes">{latestNotes}</p> : null}
          {redFlags && redFlags.length > 0
            ? redFlags.map((flag, idx) => (
                <div key={`flag-${idx}`} className="gg-sage-panel__flag">
                  Flag: {flag}
                </div>
              ))
            : null}
        </div>
      )}

      <div className="gg-sage-panel__toolbar">
        <button type="button" className="gg-sage-panel__tool" onClick={onClear}>
          Clear
        </button>
        {onDismiss ? (
          <button type="button" className="gg-sage-panel__tool gg-sage-panel__tool--danger" onClick={onDismiss}>
            Dismiss Sage
          </button>
        ) : null}
      </div>

      <div className="gg-sage-panel__body">
        {suggestions.length === 0 ? (
          <div className="gg-sage-panel__empty">
            {sessionComplete
              ? 'Sage session ended.'
              : ready
                ? 'Listening — Sage only speaks on contradictions or when you need technical help.'
                : inviting
                  ? 'Sage is joining…'
                  : 'Sage is starting…'}
          </div>
        ) : (
          suggestions.map((batch) => (
            <div key={batch.id} className="gg-sage-card gg-sage-card--live">
              {batch.case ? (
                <div className="gg-sage-item__why" style={{ marginBottom: 8 }}>
                  {batch.case === 'contradiction' ? 'Contradiction' : 'Technical assist'}
                </div>
              ) : null}
              {(batch.suggestions || []).map((item, idx) => (
                <div key={`${batch.id}-${idx}`} className="gg-sage-item">
                  <div className="gg-sage-item__top">
                    <span
                      className="gg-sage-item__priority"
                      style={{ color: priorityColor(item.priority) }}
                    >
                      {item.priority || 'medium'}
                    </span>
                    <button
                      type="button"
                      className="gg-sage-item__copy"
                      onClick={() => copyText(item.question || '')}
                    >
                      Copy
                    </button>
                  </div>
                  <div className="gg-sage-item__q">{item.question}</div>
                  {item.rationale ? (
                    <div className="gg-sage-item__why">{item.rationale}</div>
                  ) : null}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </>
  );

  return (
    <aside
      className={`gg-sage-panel${docked ? ' gg-sage-panel--docked' : ''}`}
      role="complementary"
      aria-label="Sage"
    >
      <SagePanelHeader
        subtitle={statusLabel}
        onClose={onClose}
        online
      />
      {mode === 'intro' ? (
        <SageIntro
          onAllow={onAllowJoin}
          onMaybeLater={onClose}
          busy={inviting}
          languages={SAGE_LANGUAGES}
          defaultLanguage={DEFAULT_SAGE_LANGUAGE}
        />
      ) : mode === 'context' && contextPicker ? (
        <div className="gg-sage-panel__body gg-sage-panel__body--context">
          <SageContextPicker {...contextPicker} />
        </div>
      ) : (
        activeBody
      )}
    </aside>
  );
}
